import { Injectable, signal, computed } from '@angular/core';
import { fromEvent, Observable, BehaviorSubject } from 'rxjs';
import { debounceTime, map, startWith } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class MobileDetectionService {
  private readonly MOBILE_BREAKPOINT = 768; // Tailwind's md breakpoint
  private readonly TABLET_BREAKPOINT = 1024; // Tailwind's lg breakpoint
  
  private windowWidthSubject = new BehaviorSubject<number>(window.innerWidth);
  public windowWidth$ = this.windowWidthSubject.asObservable();
  
  // Signals for reactive state
  private _isMobile = signal(this.isMobileDevice());
  private _isTablet = signal(this.isTabletDevice());
  private _isDesktop = signal(this.isDesktopDevice());
  private _screenWidth = signal(window.innerWidth);
  
  // Computed signals
  public isMobile = computed(() => this._isMobile());
  public isTablet = computed(() => this._isTablet());
  public isDesktop = computed(() => this._isDesktop());
  public screenWidth = computed(() => this._screenWidth());
  
  // Observable for reactive updates
  public isMobile$: Observable<boolean>;
  public isTablet$: Observable<boolean>;
  public isDesktop$: Observable<boolean>;

  constructor() {
    this.initializeResizeListener();
    this.initializeObservables();
  }

  private initializeResizeListener(): void {
    fromEvent(window, 'resize')
      .pipe(
        debounceTime(100),
        map(() => window.innerWidth)
      )
      .subscribe(width => {
        this._screenWidth.set(width);
        this._isMobile.set(this.isMobileDevice());
        this._isTablet.set(this.isTabletDevice());
        this._isDesktop.set(this.isDesktopDevice());
        this.windowWidthSubject.next(width);
      });
  }

  private initializeObservables(): void {
    this.isMobile$ = this.windowWidth$.pipe(
      map(width => width < this.MOBILE_BREAKPOINT),
      startWith(this.isMobileDevice())
    );

    this.isTablet$ = this.windowWidth$.pipe(
      map(width => width >= this.MOBILE_BREAKPOINT && width < this.TABLET_BREAKPOINT),
      startWith(this.isTabletDevice())
    );

    this.isDesktop$ = this.windowWidth$.pipe(
      map(width => width >= this.TABLET_BREAKPOINT),
      startWith(this.isDesktopDevice())
    );
  }

  /**
   * Check if current device is mobile based on screen width
   */
  private isMobileDevice(): boolean {
    return window.innerWidth < this.MOBILE_BREAKPOINT;
  }

  /**
   * Check if current device is tablet based on screen width
   */
  private isTabletDevice(): boolean {
    return window.innerWidth >= this.MOBILE_BREAKPOINT && window.innerWidth < this.TABLET_BREAKPOINT;
  }

  /**
   * Check if current device is desktop based on screen width
   */
  private isDesktopDevice(): boolean {
    return window.innerWidth >= this.TABLET_BREAKPOINT;
  }

  /**
   * Check if device is mobile using user agent (more reliable for initial detection)
   */
  public isMobileUserAgent(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    
    // Check for mobile user agents
    const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
    return mobileRegex.test(userAgent);
  }

  /**
   * Check if device is tablet using user agent
   */
  public isTabletUserAgent(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
    
    // Check for tablet user agents
    const tabletRegex = /ipad|android(?!.*mobile)|tablet/i;
    return tabletRegex.test(userAgent);
  }

  /**
   * Get device type as string
   */
  public getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
    if (this.isMobile()) return 'mobile';
    if (this.isTablet()) return 'tablet';
    return 'desktop';
  }

  /**
   * Check if device should show mobile WIP screen
   * Combines screen size and user agent for better detection
   */
  public shouldShowMobileWIP(): boolean {
    return this.isMobile() || (this.isMobileUserAgent() && window.innerWidth < this.TABLET_BREAKPOINT);
  }

  /**
   * Get current breakpoint name
   */
  public getCurrentBreakpoint(): 'sm' | 'md' | 'lg' | 'xl' | '2xl' {
    const width = this.screenWidth();
    if (width < 640) return 'sm';
    if (width < 768) return 'md';
    if (width < 1024) return 'lg';
    if (width < 1280) return 'xl';
    return '2xl';
  }
}
